import { Period } from '@cbo/shared-library';
import { FiscalCalendar, IPeriodFilter } from '@cbo/shared-library/response/calendar.response';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { getFiscalCalendarPeriodsFilters } from '../../utils/calendarUtils';

export const getPeriods = (fiscalCalendar?: FiscalCalendar): IPeriodFilter[] => {
  if (!fiscalCalendar) {
    // Return only calendar periods, no fiscal periods, and exclude Today
    return getFiscalCalendarPeriodsFilters({
      id: 'calendar-only',
      orgId: 'calendar-only',
      calendarType: 'fiscal',
      firstDayOfWeek: 0, // Sunday (standard calendar week)
      month: 0,
      day: 1,
      monthPattern: '4-4-5',
    }).filter((period) => {
      const key = 'key' in period.current ? period.current.key : '';
      // Only include calendar periods, exclude fiscal periods and Today
      return !key.includes('FISCAL') && key !== Period.TODAY;
    });
  }

  // Use actual fiscal calendar - includes both calendar AND fiscal periods, but exclude Today
  return getFiscalCalendarPeriodsFilters(fiscalCalendar).filter((period) => {
    const key = 'key' in period.current ? period.current.key : '';
    return key !== Period.TODAY;
  });
};

export const getPeriodLabel = (key: string): string => {
  switch (key) {
    case Period.TODAY:
      return t('sales.dateRanges.today');
    case Period.YESTERDAY:
      return t('sales.dateRanges.yesterday');
    case Period.CALENDAR_WEEK_END:
      return t('sales.dateRanges.week');
    case Period.LAST_CALENDAR_WEEK_END:
      return t('sales.dateRanges.lastWeek');
    case Period.CALENDAR_MONTH_END:
      return t('sales.dateRanges.month');
    case Period.LAST_CALENDAR_MONTH_END:
      return t('sales.dateRanges.lastMonth');
    case Period.CALENDAR_YEAR_END:
      return t('sales.dateRanges.year');
    case Period.LAST_CALENDAR_YEAR_END:
      return t('sales.dateRanges.lastYear');
    case Period.FISCAL_WEEK_END:
      return t('sales.dateRanges.fiscalWeek');
    case Period.LAST_FISCAL_WEEK_END:
      return t('sales.dateRanges.lastFiscalWeek');
    case Period.FISCAL_MONTH_END:
      return t('sales.dateRanges.fiscalMonth');
    case Period.LAST_FISCAL_MONTH_END:
      return t('sales.dateRanges.lastFiscalMonth');
    case Period.FISCAL_YEAR_END:
      return t('sales.dateRanges.fiscalYear');
    case Period.LAST_FISCAL_YEAR_END:
      return t('sales.dateRanges.lastFiscalYear');
    default:
      return key;
  }
};

export const createDateRangeMap = (periods: IPeriodFilter[]) =>
  periods.reduce((acc, period) => {
    if ('date' in period.current && 'key' in period.current) {
      acc[period.current.key] = {
        label: getPeriodLabel(period.current.key),
        date: dayjs(period.current.date).format('YYYY-MM-DD'),
        startDate: dayjs(period.current.date).startOf('day').toISOString(),
        endDate: dayjs(period.current.date).endOf('day').toISOString(),
      };
    }

    if ('startDate' in period.current && 'endDate' in period.current && 'key' in period.current) {
      acc[period.current.key] = {
        label: getPeriodLabel(period.current.key),
        startDate: dayjs(period.current.startDate as unknown as string)
          .startOf('day')
          .toISOString(),
        endDate: dayjs(period.current.endDate as unknown as string)
          .endOf('day')
          .toISOString(),
      };
    }

    return acc;
  }, {} as Record<string, { label: string; date?: string; startDate: string; endDate: string }>);
